import React from 'react';

/**
 * Renderiza texto con formato estilo WhatsApp (*negrita*, _cursiva_, listas, etc.).
 * Mismo criterio que la consola web y el demo.
 */
export function formatMessage(text: string): React.ReactNode {
  if (!text) return null;

  const codeBlockParts = text.split(/(```[\s\S]*?```)/);
  const elements: React.ReactNode[] = [];

  for (let i = 0; i < codeBlockParts.length; i++) {
    const part = codeBlockParts[i];

    if (part.startsWith('```') && part.endsWith('```')) {
      const code = part.slice(3, -3).replace(/^\w*\n/, '');
      elements.push(
        <pre key={`code-${i}`} className="chat-msg__pre">
          {code.trim()}
        </pre>
      );
    } else {
      elements.push(<FormattedText key={`text-${i}`} text={part} />);
    }
  }

  return <>{elements}</>;
}

/** Vista previa en una línea sin marcado (lista de chats, alertas). */
export function plainMessagePreview(text: string, max = 56): string {
  const t = text
    .replace(/```[\s\S]*?```/g, ' ')
    .replace(/[*_~`]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
  if (t.length <= max) return t;
  return `${t.slice(0, max - 1)}…`;
}

function FormattedText({ text }: { text: string }) {
  const lines = text.split('\n');
  const result: React.ReactNode[] = [];
  let listBuffer: { type: 'ul' | 'ol'; items: React.ReactNode[] } | null = null;

  function flushList() {
    if (!listBuffer) return;
    const Tag = listBuffer.type;
    result.push(
      <Tag key={`list-${result.length}`} className="chat-msg__list">
        {listBuffer.items.map((item, idx) => (
          <li key={idx}>{item}</li>
        ))}
      </Tag>
    );
    listBuffer = null;
  }

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const ulMatch = line.match(/^[\-•]\s+(.+)/);
    if (ulMatch) {
      if (!listBuffer || listBuffer.type !== 'ul') {
        flushList();
        listBuffer = { type: 'ul', items: [] };
      }
      listBuffer.items.push(formatInline(ulMatch[1]));
      continue;
    }

    const olMatch = line.match(/^\d+\.\s+(.+)/);
    if (olMatch) {
      if (!listBuffer || listBuffer.type !== 'ol') {
        flushList();
        listBuffer = { type: 'ol', items: [] };
      }
      listBuffer.items.push(formatInline(olMatch[1]));
      continue;
    }

    flushList();

    if (line.trim() === '') {
      if (i > 0 && i < lines.length - 1) result.push(<br key={`br-${i}`} />);
      continue;
    }

    result.push(
      <span key={`line-${i}`}>
        {i > 0 && result.length > 0 && <br />}
        {formatInline(line)}
      </span>
    );
  }

  flushList();
  return <>{result}</>;
}

function formatInline(text: string): React.ReactNode {
  const parts = text.split(/(`[^`]+`)/);
  const elements: React.ReactNode[] = [];

  for (let i = 0; i < parts.length; i++) {
    const part = parts[i];
    if (part.startsWith('`') && part.endsWith('`')) {
      elements.push(
        <code key={i} className="chat-msg__code">
          {part.slice(1, -1)}
        </code>
      );
    } else {
      elements.push(<InlineFormatted key={i} text={part} />);
    }
  }

  return <>{elements}</>;
}

function InlineFormatted({ text }: { text: string }) {
  const regex = /(\*[^*\n]+\*|_[^_\n]+_|~[^~\n]+~)/;
  const parts = text.split(regex);

  if (parts.length === 1) return <>{text}</>;

  return (
    <>
      {parts.map((part, i) => {
        if (part.startsWith('*') && part.endsWith('*') && part.length > 2) {
          return <strong key={i}>{part.slice(1, -1)}</strong>;
        }
        if (part.startsWith('_') && part.endsWith('_') && part.length > 2) {
          return <em key={i}>{part.slice(1, -1)}</em>;
        }
        if (part.startsWith('~') && part.endsWith('~') && part.length > 2) {
          return <s key={i}>{part.slice(1, -1)}</s>;
        }
        return <React.Fragment key={i}>{part}</React.Fragment>;
      })}
    </>
  );
}
